import { Loader } from '@wheely/ui-kit';
import debounce from 'lodash/debounce';
import React, { useState, useMemo, useEffect } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getPaymentInfoByBIK } from '@/modules/partner/actions/async/getPaymentInfoByBIK';
import { PaymentInfoForm } from '@/modules/partner/model/form';
import { TPartnerType } from '@/modules/partner/model/partnerType';
import { AppDispatch } from '@/root-store';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';

import s from './styles.module.css';

type Props = {
  control: Control<PaymentInfoForm>;
  partnerType?: TPartnerType;
};

const bikValidationRegex = /^\d{9}$/;

const RuPaymentInfo: React.FC<Props> = ({ control, partnerType }) => {
  const dispatch: AppDispatch = useDispatch();
  const intl = useIntl();
  const { watch, setError, clearErrors, setValue } = useFormContext<PaymentInfoForm>();

  const [isLoading, setIsLoading] = useState(false);

  const [bik, corAccount, bankName] = watch(['bik', 'corAccount', 'bankName']);

  const fetchAdditionalPaymentInfo = useMemo(
    () =>
      debounce(async (newBik: string) => {
        clearErrors('bik');
        setIsLoading(true);

        try {
          const result = await dispatch(getPaymentInfoByBIK(newBik)).unwrap();

          if (result.paymentBankName && result.paymentKorAccount) {
            setValue('bankName', result.paymentBankName);
            setValue('corAccount', result.paymentKorAccount);
          }

          if ('isError' in result) {
            setError('bik', {
              message: intl.formatMessage({
                defaultMessage: 'Incorrect BIC',
                id: 'FFzE1r',
                description: 'BIC error message',
              }),
            });
          }
        } finally {
          setIsLoading(false);
        }
      }, 200),
    [dispatch, setIsLoading, setError, clearErrors, intl, setValue],
  );

  useEffect(() => {
    const getBankNameAndCorAccount = async () => {
      const isBikValid = new RegExp(bikValidationRegex).test(bik);

      if (isBikValid) {
        fetchAdditionalPaymentInfo(bik);
      }
    };

    getBankNameAndCorAccount();
  }, [bik, dispatch, fetchAdditionalPaymentInfo]);

  return (
    <>
      <TextField
        name="rasAccount"
        title={intl.formatMessage({
          defaultMessage: 'ACCOUNT NUMBER',
          id: 'n8n0/+',
          description: 'Account number field title',
        })}
        placeholder={intl.formatMessage({
          defaultMessage: '4080181000000600001',
          id: '2p4yS8',
          description: 'RU Account number field placeholder',
        })}
        description={intl.formatMessage(
          {
            defaultMessage: `{partnerType, select,
            Self_Employed {Account held in your name.}
            ИП {Account opened in the name of the individual entrepreneur.}
            Юридическое_лицо {Account in the name of the company.}
            other {}
        }`,
            id: '4qvmrq',
            description: 'RU Account number field description',
          },
          { partnerType: (partnerType ?? 'other').replace(/\s/g, '_') },
        )}
        control={control}
        rules={{
          required: true,
          pattern: {
            value: /^\d{20}$/,
            message: intl.formatMessage({
              defaultMessage: 'This is 20 digits.',
              id: '0rf3pP',
              description: 'Field error message',
            }),
          },
        }}
      />
      <TextField
        name="bik"
        title={intl.formatMessage({
          defaultMessage: 'BIC',
          id: 'TcbaS8',
          description: 'BIC field title',
        })}
        placeholder={intl.formatMessage({
          defaultMessage: '044525225',
          id: 'kVUU8D',
          description: 'BIK field placeholder',
        })}
        control={control}
        rules={{
          required: true,
          pattern: {
            value: bikValidationRegex,
            message: intl.formatMessage({
              defaultMessage: 'This is 9 digits.',
              id: 'NWDmx1',
              description: 'Field error message',
            }),
          },
        }}
      />
      {corAccount && bankName ? (
        <>
          <TextField
            isDisabled={true}
            name="bankName"
            title={
              <div className={s.textFieldLabel}>
                <FormattedMessage
                  defaultMessage="Bank name"
                  id="NfDBdG"
                  description="Bank name field title"
                />
                {isLoading && <Loader className={s.textFieldLoader} />}
              </div>
            }
            control={control}
          />
          <TextField
            isDisabled={true}
            name="corAccount"
            title={
              <div className={s.textFieldLabel}>
                <FormattedMessage
                  defaultMessage="Correspondent account"
                  id="nkBpTV"
                  description="Correspondent account field title"
                />
                {isLoading && <Loader className={s.textFieldLoader} />}
              </div>
            }
            control={control}
          />
        </>
      ) : (
        isLoading && <Loader className={s.loader} />
      )}
    </>
  );
};

export { RuPaymentInfo };
